/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {getUserByToken, login} from '../core/_requests'
import {useAuth} from '../core/Auth'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import {decryptId} from '_metronic/helpers/Encrypt'
import {showErrorMessage} from '_metronic/helpers/swalHelper'
import {SetCampus} from '../../../../redux/Slice/userSlice'
import {useAppDispatch} from '../../../../redux/hooks'

const initialValues = {
  username: localStorage.getItem('username') || '',
  password: localStorage.getItem('password') ? decryptId(localStorage.getItem('password')) : '',
  rememberMe: false,
}

export function Login() {
  const {t} = useTranslation()
  const [showCampusModal, setShowCampusModal] = useState(false)
  const [rightCampuses, setRightCampuses] = useState<any>([])
  const [leftCampuses, setLeftCampuses] = useState<any>([])
  const dispatch = useAppDispatch()
  const loginSchema = Yup.object().shape({
    username: Yup.string()
      .min(3, t('VALIDATION.MIN_CHARACTER_', {quantity: 3}))
      .max(100, t('VALIDATION.MAX_CHARACTER_', {quantity: 100}))
      .required(t('VALIDATION.REQUIRED_', {name: t('GLOBAL.USERNAME')})),
    password: Yup.string()
      .min(3, t('VALIDATION.MIN_CHARACTER_', {quantity: 3}))
      .max(50, t('VALIDATION.MAX_CHARACTER_', {quantity: 50}))
      .required(t('VALIDATION.REQUIRED_', {name: t('GLOBAL.PASSWORD')})),
  })
  const [loading, setLoading] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const {data: auth} = await login(values.username, values.password)
        saveAuth(auth)

        if (auth.permissions?.includes('access-to-all-campus-data')) {
          // Temporarily save campuses and token
          setRightCampuses(auth?.right_campuses)
          setLeftCampuses(auth?.left_campuses)
          setShowCampusModal(true)
          setLoading(false)
          return
        }

        // Otherwise, normal user -> fetch user and redirect
        const {data: user} = await getUserByToken(auth.api_token)
        setCurrentUser(user)
      } catch (error: any) {
        if (error.response?.data) {
          if (error.response.status === 404) {
            setStatus(t('MESSAGES.NOT_FOUND_ERROR'))
          } else {
            setStatus(error.response.data.message)
          }
        } else {
          console.log('Error', error.message)
        }
        saveAuth(undefined)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const handleCampusSelect = async (campusId: any) => {
    const formData = new FormData()
    formData.append('campus_id', campusId)
    dispatch(SetCampus(formData)).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setCurrentUser(res.user)
        setShowCampusModal(false)
        window.location.href = '/dashboard'
      } else if (res.meta.requestStatus === 'rejected') {
        showErrorMessage(t('MESSAGES.SERVER_ERROR'))
      }
    })
  }

  return (
    <>
      <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
        <div className='text-center mb-5'>
          <h2 className='fw-bold text-dark mb-1'>{t('GLOBAL.LOGIN_PAGE')}</h2>
        </div>
        {formik.status && (
          <div className='alert alert-danger mb-4'>
            <div className='alert-text fw-bold'>{formik.status}</div>
          </div>
        )}

        <div className='fv-row mb-4'>
          <label className='form-label fw-bold text-primary'>{t('GLOBAL.USERNAME')}</label>
          <input
            type='text'
            placeholder='ex: Djawed'
            {...formik.getFieldProps('username')}
            className={clsx(
              'form-control form-control-lg',
              formik.touched.username && formik.errors.username && 'is-invalid',
              formik.touched.username && !formik.errors.username && 'is-valid'
            )}
          />

          {formik.touched.username && formik.errors.username && (
            <div className='text-danger mt-1 fw-bold fs-7'>{formik.errors.username}</div>
          )}
        </div>

        {/* Password */}
        <div className='fv-row mb-4'>
          <label className='form-label fw-bold text-primary'>{t('GLOBAL.PASSWORD')}</label>

          <input
            type='password'
            placeholder='********'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control form-control-lg',
              formik.touched.password && formik.errors.password && 'is-invalid',
              formik.touched.password && !formik.errors.password && 'is-valid'
            )}
          />

          {formik.touched.password && formik.errors.password && (
            <div className='text-danger mt-1 fw-bold fs-7'>{formik.errors.password}</div>
          )}
        </div>

        <div className='d-flex justify-content-between align-items-center mb-4'>
          <label className='form-check m-0 fw-bold text-primary'>
            <input
              type='checkbox'
              className='form-check-input mx-3 fw-bold'
              name='rememberMe'
              checked={formik.values.rememberMe}
              onChange={formik.handleChange}
            />
            {t('GLOBAL.REMEMBER_ME')}
          </label>

          <Link to='/forgot-password' className='fw-bold text-primary'>
            {t('GLOBAL.FORGOT_PASSWORD')}
          </Link>
        </div>

        <button
          type='submit'
          className='btn btn-primary w-100 py-3 fs-5 fw-bold rounded'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span>{t('GLOBAL.LOGIN')}</span>}
          {loading && <span className='spinner-border spinner-border-sm ms-2'></span>}
        </button>

        <div className='text-center mt-6'>
          <a
            href='https://youtu.be/AALLzu7xIMs'
            className='fw-bold text-muted'
            target='_blank'
            rel='noopener noreferrer'
          >
            <i className='fa fa-video-camera mx-3'></i>
            {t('GLOBAL.HOW_TO_PLATFORM')}
          </a>
        </div>
      </form>

      {showCampusModal && (
        <div
          className='modal show fade d-block'
          tabIndex={-1}
          style={{backgroundColor: 'rgba(0,0,0,0.5)'}}
        >
          <div className='modal-dialog modal-dialog-centered modal-xl'>
            <div className='modal-content'>
              <div className='modal-header p-3 bg-primary'>
                <h5 className='modal-title text-white'>{t('Select Campus')}</h5>
              </div>
              <div className='modal-body'>
                <div className='row'>
                  {/* Left Campuses */}
                  <div className='col-md-6'>
                    <div className='row g-3'>
                      {rightCampuses.map((campus: any) => (
                        <div key={campus.id} className='col-12'>
                          <div
                            className='card border-success text-center cursor-pointer'
                            onClick={() => handleCampusSelect(campus.id)}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor = '#f8f9fa')
                            }
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                          >
                            <div className='card-body p-2'>
                              <h6 className='card-title mb-0'>{campus.name}</h6>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Right Campuses */}
                  <div className='col-md-6'>
                    <div className='row g-3'>
                      {leftCampuses?.map((campus: any) => (
                        <div key={campus.id} className='col-12'>
                          <div
                            className='card border-primary text-center cursor-pointer'
                            onClick={() => handleCampusSelect(campus.id)}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor = '#f8f9fa')
                            }
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                          >
                            <div className='card-body p-2'>
                              <h6 className='card-title mb-0'>{campus.name}</h6>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
